import FetchingSelect from "@/components/FetchingSelect";
import ImageCell from "@/components/ImageCell";
import ImageUploader from "@/components/ImageUploader";
import { AdvanceShareStyleAssetTypeEnum, AdvanceShareStyleAssetTypeList } from "@/models/advance-share-style-definition.model";
import { AdvanceShareAsset } from "@/models/advance-share.model";
import { ObjectStorageFileCategory } from "@/models/storage.model";
import { getBrandGearsLookup } from "@/services/brand-gear.service";
import React from "react";
import { useState } from "react";
import { Button, Col, Modal, ModalProps, Panel, Row, Table } from "rsuite";
import Cell from "rsuite/esm/Table/TableCell";
import Column from "rsuite/esm/Table/TableColumn";
import HeaderCell from "rsuite/esm/Table/TableHeaderCell";

interface AssetListProps extends ModalProps{
  value: Record<string, AdvanceShareAsset>;
  onChange: (val: Record<string, AdvanceShareAsset>) => void;
};

// Show up to 3 rows below the header, then scroll inside the table.
const MAX_VISIBLE_ROWS = 3;
const ROW_HEIGHT = 56;
const TABLE_MAX_HEIGHT = 40 + MAX_VISIBLE_ROWS * ROW_HEIGHT;

const AssetList: React.FC<AssetListProps> = ({value, onChange}) => {
    const [selectedKey, setSelectedKey] = useState<string>();
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    
    return (
        <>
            <Panel bordered header='Assets'>
                <Table data={Object.entries(value).map(([key, val]) => ({ key, type: val.type, url: val.url, id: val.id }))} autoHeight maxHeight={TABLE_MAX_HEIGHT} rowHeight={ROW_HEIGHT}>
                    <Column flexGrow={1} align="start" sortable>
                        <HeaderCell>Key</HeaderCell>
                        <Cell dataKey="key" />
                    </Column>
                    <Column flexGrow={1} align="start" sortable>
                        <HeaderCell>Type</HeaderCell>
                        <Cell>
                            {(rowData: AdvanceShareAsset) => AdvanceShareStyleAssetTypeList.findLast(t => t.value == rowData.type)?.label ?? ''}
                        </Cell>
                    </Column>
                    <Column flexGrow={1} align="start" sortable>
                        <HeaderCell>Id</HeaderCell>
                        <Cell dataKey="id" />
                    </Column>
                    <Column flexGrow={1} align="start" sortable>
                        <HeaderCell>Image</HeaderCell>
                        <ImageCell dataKey="url" />
                    </Column>
                    <Column flexGrow={1} align='center'>
                        <HeaderCell>Actions</HeaderCell>
                        <Cell>
                        {rowData => (
                            <Button 
                            size="xs" 
                            appearance='ghost' 
                            onClick={() => {
                                setEditModalOpen(true);
                                setSelectedKey(rowData.key);
                            }} > Edit </Button>
                        )}
                        </Cell>
                    </Column>                                        
                </Table>
            </Panel>
            {selectedKey && 
                <AssetModal 
                    open={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    value={value[selectedKey]} 
                    onChange={(val => {
                        const updated = {...value};
                        updated[selectedKey] = val;
                        onChange(updated);
                    })} 
                />
            }
        </>
    );
};

export default AssetList;


interface AssetModalProps extends ModalProps{
  value?: AdvanceShareAsset;
  onChange: (val: AdvanceShareAsset) => void;
};

const AssetModal: React.FC<AssetModalProps> = ({value, onChange, open, onClose}) => {

    return(
      <Modal backdrop='static' keyboard={false} open={open} onClose={onClose}>
        <Modal.Header>
          <Modal.Title>Asset ({AdvanceShareStyleAssetTypeList.findLast(a => a.value == value?.type)?.label ?? ''})</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <ImageUploader
                height="300px"
                url={value?.url}
                objectStorageCategory={ObjectStorageFileCategory.AdvanceShare}
                clearable={true}
                uploadable={true}
                onChange={val => onChange({...value, url:val , id: val ? undefined : value?.id})}
            />
            {value?.type == AdvanceShareStyleAssetTypeEnum.GearImage && 
            <FetchingSelect block
                fetchData={(text, page) => getBrandGearsLookup(text, page)} 
                placeholder="Select a gear"
                onChange={val => onChange({...value, id:val , url: val ? undefined : value?.url})}
            />} 
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} appearance="primary">
            Ok
          </Button>
        </Modal.Footer>
      </Modal>);
};
